/**
 * Janela 24h do WhatsApp Business (customer service window).
 *
 * Fonte de verdade (preenchida pelos webhooks da 360dialog/Meta):
 * - `meta_window_expires_at`: timestamp oficial vindo do objeto
 *   `statuses[].conversation.expiration_timestamp` (unix segundos) da Meta.
 *
 * Fallback: `last_inbound_at + 24h` quando ainda não recebemos nenhum
 * status webhook com o objeto `conversation` (ex: conversa muito antiga).
 *
 * O webhook inbound também seta `meta_window_expires_at = now+24h` no
 * mesmo update do `last_inbound_at`, garantindo que o composer libere
 * imediatamente quando o cliente responde.
 */

export interface WindowSource {
  meta_window_expires_at?: string | null;
  last_inbound_at?: string | null;
  /** Último outbound — usado como fallback final quando o cliente nunca respondeu */
  last_outbound_at?: string | null;
  /**
   * Provider da conexão. A janela 24h é regra da **API oficial da Meta**, então
   * vale para `360dialog` (Cloud API, hoje descomissionado) e para `bee360`
   * (04/08 — a linha do Bee atende pela Cloud API). `evolution` (Baileys) NÃO
   * tem essa restrição: conversa sempre aberta, chip não aparece.
   *
   */
  connection_provider?: string | null;
  /** can_reply do último evento do Bee (a palavra dele sobre texto livre). */
  bee_pode_responder?: boolean | null;
  bee_pode_responder_em?: string | null;
}

export function getWindowExpiresAt(conv: WindowSource | null | undefined): number | null {
  if (!conv) return null;
  // Janela 24h é regra da API oficial da Meta: vale pra 360dialog (Cloud API,
  // descomissionado) e pra bee360 (a linha do Bee atende pela Cloud API).
  // Evolution e provider nulo/desconhecido NÃO têm essa regra — conversa sempre
  // aberta, nada é exibido.
  if (conv.connection_provider !== '360dialog' && conv.connection_provider !== 'bee360') return null;
  // Cálculo robusto: usamos last_inbound_at + 24h como base mínima.
  // O timestamp oficial da Meta (`meta_window_expires_at`) só vence se for MAIOR
  // que esse cálculo — em produção a 360dialog já retornou expirations menores
  // que o inbound real, o que travava o composer indevidamente.
  // O Bee disse que NÃO pode responder depois da última mensagem do cliente:
  // fechada, seja qual for a conta (Douglas 25/09/2026: "usar o que é").
  // Mesma régua do backend em janela-oficial.service.ts.
  const beeEm = conv.bee_pode_responder_em ? new Date(conv.bee_pode_responder_em).getTime() : 0;
  const inboundEm = conv.last_inbound_at ? new Date(conv.last_inbound_at).getTime() : 0;
  if (conv.connection_provider === 'bee360' && conv.bee_pode_responder === false && beeEm >= inboundEm) return 0;

  const fromInbound = conv.last_inbound_at
    ? new Date(conv.last_inbound_at).getTime() + 24 * 60 * 60 * 1000
    : null;
  const fromMeta = conv.meta_window_expires_at
    ? new Date(conv.meta_window_expires_at).getTime()
    : null;

  if (fromInbound !== null && fromMeta !== null) {
    return Math.max(fromInbound, fromMeta);
  }
  if (fromInbound !== null) return fromInbound;
  if (fromMeta !== null) return fromMeta;

  // Cliente NUNCA respondeu.
  // Pelas regras oficiais do WhatsApp Business, a customer service window
  // de 24h SÓ é aberta por uma mensagem inbound do cliente. Templates
  // outbound não abrem janela. Portanto: sem inbound = janela FECHADA,
  // independente de quando foi o último outbound.
  if (conv.last_outbound_at) {
    return 0; // epoch — sempre no passado, força estado "expirada"
  }
  return null;
}

export interface WindowChip {
  label: string;
  /** Tailwind classes para o chip */
  cls: string;
  /** true se a janela já expirou */
  expired: boolean;
}

export interface WindowGhost {
  /** Texto curto, ex: "14h20", "45min", "expirada" */
  label: string;
  /** Apenas classe de cor de texto (sem fundo nem borda) */
  textCls: string;
  expired: boolean;
}

/**
 * Variante "ghost" do chip: só ícone + texto colorido, sem fundo nem borda.
 * Usado nos cards e no header pra um visual mais sutil.
 */
export function getWindowGhost(
  conv: WindowSource | null | undefined,
  now: number = Date.now(),
): WindowGhost | null {
  const expiresAt = getWindowExpiresAt(conv);
  if (expiresAt === null) return null;

  const msLeft = expiresAt - now;
  if (msLeft < 0) {
    return {
      label: 'expirada',
      // Paleta da casa: fechada/acabando = bordô; aberta com folga = neutro.
      // (Era rosa e verde — verde no CRM é só desfecho.)
      textCls: 'text-[#5B0A0D] dark:text-[#C87484]',
      expired: true,
    };
  }
  const hours = Math.floor(msLeft / 3_600_000);
  const mins = Math.floor((msLeft % 3_600_000) / 60_000);
  const fmt = hours > 0 ? `${hours}h${mins.toString().padStart(2, '0')}` : `${mins}min`;

  if (msLeft < 6 * 3_600_000) {
    return {
      label: fmt,
      textCls: 'text-[#5B0A0D] dark:text-[#C87484]',
      expired: false,
    };
  }
  return {
    label: fmt,
    textCls: 'text-muted-foreground',
    expired: false,
  };
}

/**
 * Devolve label + cores para o chip da janela 24h.
 * - Verde: > 6h restantes
 * - Amarelo: < 6h restantes
 * - Vermelho: expirada
 */
export function getWindowChip(
  conv: WindowSource | null | undefined,
  now: number = Date.now(),
): WindowChip | null {
  const expiresAt = getWindowExpiresAt(conv);
  if (expiresAt === null) return null;

  const msLeft = expiresAt - now;
  if (msLeft < 0) {
    return {
      label: 'Janela expirada',
      cls: 'bg-danger/10 text-danger border-danger/40 dark:bg-danger/40 dark:text-danger dark:border-danger',
      expired: true,
    };
  }

  const hours = Math.floor(msLeft / (60 * 60 * 1000));
  const mins = Math.floor((msLeft % (60 * 60 * 1000)) / (60 * 1000));
  const fmt = hours > 0 ? `${hours}h${mins.toString().padStart(2, '0')}` : `${mins}min`;

  if (msLeft < 6 * 60 * 60 * 1000) {
    return {
      label: `Janela ${fmt}`,
      cls: 'bg-primary/10 text-primary border-primary/40 dark:bg-primary/40 dark:text-primary dark:border-primary',
      expired: false,
    };
  }

  return {
    label: `Janela ${fmt}`,
    cls: 'bg-success/10 text-success border-success/40 dark:bg-success/40 dark:text-success dark:border-success',
    expired: false,
  };
}
